import React, { useMemo } from 'react';

import CurrencyFormField from '@components/form-fields/CurrencyFormField';
import DatePickerFormField from '@components/form-fields/DatePickerFormField';
import PercentageFormField from '@components/form-fields/PercentageFormField';
import SelectFormField from '@components/form-fields/SelectFormField';
import TextInputFormField from '@components/form-fields/TextInputFormField';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import PrecisionService from '@core/services/PrecisionService';
import { useHydrateMarineFromQuery } from '@features/production/proposal/hooks/useHydrateMarineFromQuery';
import { useCalculateBasicRate } from '@features/production/proposal/proposal.slice';

import { useGetAmendmentMarineLovs } from '../amendment.slice';
import { FormValues } from '../types';

import { useFormikContext } from 'formik';
import { makeStyles } from 'tss-react/mui';

type CurrencyLov = { id: string; code: string; title: string; symbol: string };
type MatterLov = { id: string; code: string; title: string };

type Props = {
  disabledForm: boolean;
  policyCurrency: { id: string; code: string; title: string; symbol: string };
};

const useStyles = makeStyles()(() => ({
  fullFieldRow: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 12,
    marginBottom: 12
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: 12,
    marginBottom: 12
  },
  row5Field: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 12,
    marginBottom: 12
  }
}));

const MarineWidgetAtom: React.FC<Props> = ({
  disabledForm,
  policyCurrency
}) => {
  const { classes } = useStyles();

  const { data: lovsData } = useGetAmendmentMarineLovs();

  const currencies: CurrencyLov[] = lovsData?.core?.getCurrencies ?? [];
  const matters: MatterLov[] = lovsData?.production?.getMatters ?? [];

  const currencySymbolsMap = useMemo<Record<string, string>>(
    () =>
      (currencies ?? []).reduce(
        (acc, c) => {
          acc[c.id] = c.symbol;

          return acc;
        },
        {} as Record<string, string>
      ),
    [currencies]
  );

  const mattersOptions = useMemo<Record<string, string>>(
    () =>
      (matters ?? []).reduce(
        (acc, m) => {
          acc[m.id] = `${m.code} - ${m.title}`;

          return acc;
        },
        {} as Record<string, string>
      ),
    [matters]
  );

  const currenciesOptions = useMemo<Record<string, string>>(
    () =>
      (currencies ?? []).reduce(
        (acc, c) => {
          acc[c.id] = ` ${c.title}`;

          return acc;
        },
        {} as Record<string, string>
      ),
    [currencies]
  );

  const { values, errors, touched, setFieldValue, handleBlur } =
    useFormikContext<FormValues>();

  const marineDetails = values.marineDetails;
  const [calcRate] = useCalculateBasicRate();

  const { recalcPolicyCurrency } = useHydrateMarineFromQuery({
    proposalId: '',
    setFieldValue,
    policyCurrency,
    calcRate
  });

  return (
    <WidgetPaper>
      <WidgetSection title="Marine Details">
        <div className={classes.fullFieldRow}>
          <SelectFormField
            name="marineDetails.matterID"
            title="Matter*"
            placeholder="Matter"
            value={marineDetails.matterID}
            error={
              touched?.marineDetails?.matterID
                ? (errors as any)?.marineDetails?.matterID
                : ''
            }
            onBlur={(_, e) => handleBlur(e)}
            onChange={(v) => setFieldValue('marineDetails.matterID', v)}
            disabled={disabledForm}
            material
            fullWidth
            selectOptions={mattersOptions}
          />
        </div>

        <div className={classes.fullFieldRow}>
          <TextInputFormField
            name="marineDetails.description"
            title="Description*"
            placeholder="Description"
            value={marineDetails.description}
            error={
              touched?.marineDetails?.description
                ? (errors as any)?.marineDetails?.description
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(v) =>
              setFieldValue('marineDetails.description', v.target.value)
            }
            disabled={disabledForm}
            material
            fullWidth
          />
        </div>

        <div className={classes.fieldRow}>
          <TextInputFormField
            name="marineDetails.vessalName"
            title="Vessal Name*"
            placeholder="Vessel Name"
            value={marineDetails.vessalName}
            error={
              touched?.marineDetails?.vessalName
                ? (errors as any)?.marineDetails?.vessalName
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(e) =>
              setFieldValue('marineDetails.vessalName', e.target.value)
            }
            disabled={disabledForm}
            material
          />
          <CurrencyFormField
            name="marineDetails.vessalAge"
            title="Vessal Age"
            placeholder="Vessal Age"
            value={marineDetails.vessalAge}
            error={
              touched?.marineDetails?.vessalAge
                ? (errors as any)?.marineDetails?.vessalAge
                : ''
            }
            onBlur={(e) => {
              handleBlur(e);
            }}
            onChange={(e) =>
              setFieldValue('marineDetails.vessalAge', e.target.value)
            }
            disabled={disabledForm}
            material
            maxDecimalPrecision={0}
          />
          <TextInputFormField
            name="marineDetails.departureFrom"
            title="Departure*"
            placeholder="Departure"
            value={marineDetails.departureFrom}
            error={
              touched?.marineDetails?.departureFrom
                ? (errors as any)?.marineDetails?.departureFrom
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(e) =>
              setFieldValue('marineDetails.departureFrom', e.target.value)
            }
            disabled={disabledForm}
            material
          />
          <TextInputFormField
            name="marineDetails.departureTo"
            title="Destination*"
            placeholder="Destination"
            value={marineDetails.departureTo}
            error={
              touched?.marineDetails?.departureTo
                ? (errors as any)?.marineDetails?.departureTo
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(e) =>
              setFieldValue('marineDetails.departureTo', e.target.value)
            }
            disabled={disabledForm}
            material
          />
        </div>

        <div className={classes.fieldRow}>
          <TextInputFormField
            name="marineDetails.transportationVia"
            title="Transportation Via*"
            placeholder="Transportation Via"
            value={marineDetails.transportationVia}
            error={
              touched?.marineDetails?.transportationVia
                ? (errors as any)?.marineDetails?.transportationVia
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(v) =>
              setFieldValue('marineDetails.transportationVia', v.target.value)
            }
            disabled={disabledForm}
            material
          />
          <TextInputFormField
            name="marineDetails.survey"
            title="Survey*"
            placeholder="Survey"
            value={marineDetails.survey}
            error={
              touched?.marineDetails?.survey
                ? (errors as any)?.marineDetails?.survey
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(v) =>
              setFieldValue('marineDetails.survey', v.target.value)
            }
            disabled={disabledForm}
            material
          />
          <TextInputFormField
            name="marineDetails.cargoContract"
            title="Cargo Contract*"
            placeholder="Cargo Contract"
            value={marineDetails.cargoContract}
            error={
              touched?.marineDetails?.cargoContract
                ? (errors as any)?.marineDetails?.cargoContract
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(v) =>
              setFieldValue('marineDetails.cargoContract', v.target.value)
            }
            disabled={disabledForm}
            material
          />
          <TextInputFormField
            name="marineDetails.bank"
            title="Bank"
            placeholder="Bank"
            value={marineDetails.bank}
            error={
              touched?.marineDetails?.bank
                ? (errors as any)?.marineDetails?.bank
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(v) =>
              setFieldValue('marineDetails.bank', v.target.value)
            }
            disabled={disabledForm}
            material
          />
        </div>



        <div className={classes.fieldRow}>
          <TextInputFormField
            name="marineDetails.lcNumber"
            title="LC Number"
            placeholder="LC Number"
            value={marineDetails.lcNumber}
            error={
              touched?.marineDetails?.lcNumber
                ? (errors as any)?.marineDetails?.lcNumber
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(v) =>
              setFieldValue('marineDetails.lcNumber', v.target.value)
            }
            disabled={disabledForm}
            material
          />
          <DatePickerFormField
            name="marineDetails.lcIssueDate"
            title="LC Issue Date"
            placeholder="LC Issue Date"
            value={marineDetails.lcIssueDate ?? null}
            error={
              touched?.marineDetails?.lcIssueDate
                ? (errors as any)?.marineDetails?.lcIssueDate
                : ''
            }
            onDateChange={(v) => setFieldValue('marineDetails.lcIssueDate', v)}
            disabled={disabledForm}
            material
          />
          <TextInputFormField
            name="marineDetails.claimsPayableBy"
            title="Claims Payable By*"
            placeholder="Claims Payable By"
            value={marineDetails.claimsPayableBy}
            error={
              touched?.marineDetails?.claimsPayableBy
                ? (errors as any)?.marineDetails?.claimsPayableBy
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(v) =>
              setFieldValue('marineDetails.claimsPayableBy', v.target.value)
            }
            disabled={disabledForm}
            material
          />
          <PercentageFormField
            name="marineDetails.loading"
            title="Loading*"
            placeholder="Loading"
            value={marineDetails.loading}
            error={
              touched?.marineDetails?.loading
                ? (errors as any)?.marineDetails?.loading
                : ''
            }
            onBlur={(e) => {
              handleBlur(e);
            }}
            onChange={(v) =>
              setFieldValue('marineDetails.loading', v.target.value)
            }
            disabled={disabledForm}
            material
          />
          <div />
        </div>

        <div className={classes.row5Field}>
          <SelectFormField
            name="marineDetails.sumPerShipmentCurrencyID"
            title="Sum Per Shipment (Currency)*"
            placeholder="Currency"
            value={marineDetails.sumPerShipmentCurrencyID}
            error={
              touched?.marineDetails?.sumPerShipmentCurrencyID
                ? (errors as any)?.marineDetails?.sumPerShipmentCurrencyID
                : ''
            }
            onBlur={async (v, e) => {
              await setFieldValue(
                'marineDetails.sumPerShipmentCurrency.symbol',
                currencySymbolsMap[v]
              );

              await recalcPolicyCurrency(v, marineDetails.sumPerShipment);
            }}
            onChange={(v) =>
              setFieldValue('marineDetails.sumPerShipmentCurrencyID', v)
            }
            disabled={disabledForm}
            material
            selectOptions={currenciesOptions}
          />
          <CurrencyFormField
            name="marineDetails.sumPerShipment"
            title="Sum Per Shipment*"
            placeholder="Sum Per Shipment"
            value={marineDetails.sumPerShipment}
            error={
              touched?.marineDetails?.sumPerShipment
                ? (errors as any)?.marineDetails?.sumPerShipment
                : ''
            }
            onChange={async (e) => {
              setFieldValue('marineDetails.sumPerShipment', e.target.value);
              setFieldValue(
                'marineDetails.sumPerShipmentPolicyCurrency',
                PrecisionService.round(
                  Number(e.target.value) * marineDetails.sumPerShipmentRate,
                  3
                )
              );
            }}
            onBlur={(e) => handleBlur(e)}
            disabled={disabledForm}
            material
            currencySymbol={
              marineDetails.sumPerShipmentCurrency.symbol ??
              policyCurrency.symbol
            }
          />

          <input type="hidden" name="marineDetails.sumPerShipmentToBasicRate" />
          <input type="hidden" name="marineDetails.sumPerShipmentRate" />

          <CurrencyFormField
            name="marineDetails.sumPerShipmentPolicyCurrency"
            title="Sum Per Shipment (Policy Currency)"
            value={marineDetails.sumPerShipmentPolicyCurrency}
            error={
              touched?.marineDetails?.sumPerShipmentPolicyCurrency
                ? (errors as any)?.marineDetails?.sumPerShipmentPolicyCurrency
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={() => null}
            disabled
            material
            currencySymbol={policyCurrency.symbol}
          />
        </div>

        <div className={classes.fullFieldRow}>
          <TextInputFormField
            name="marineDetails.remarks"
            title="Remarks"
            placeholder="Remarks"
            value={marineDetails.remarks}
            error={
              touched?.marineDetails?.remarks
                ? (errors as any)?.marineDetails?.remarks
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(v) =>
              setFieldValue('marineDetails.remarks', v.target.value)
            }
            disabled={disabledForm}
            material
            fullWidth
          />
        </div>
      </WidgetSection>
    </WidgetPaper>
  );
};

export { MarineWidgetAtom };
